/**
 * 游戏注册表
 * ------------------------------------------------------------
 * 游戏 = 「元数据 + 一个 Game 类」。新增游戏不会影响已有游戏：
 *   1. 在 src/games/<your-game>/index.js 里写类并调用 registerGame()
 *   2. 在 data/games.json 里加一条元数据（图标、简介、支持的练习类型）
 *   3. 在 src/games/index.js 里 import 一行（让代码被打包进来）
 *
 * Game 类约定（可继承 GameBase）：
 *   constructor({ root, questions, content, onFinish })
 *   start()      渲染第一屏
 *   destroy()    清理定时器/事件
 *
 * 游戏只读教材数据、只调 grade()，不写自己的判分逻辑，
 * 这样「游戏表现」与「练习正确率」口径一致。
 */
import { grade } from '../engines/graders/index.js';

const registry = new Map();

export function registerGame(meta, create) {
  if (!meta?.id || typeof create !== 'function') {
    throw new Error('[games] 注册失败：需要 meta.id 与 create()');
  }
  registry.set(meta.id, { meta, create });
  return meta.id;
}

export const gameRegistry = {
  get: id => registry.get(id) || null,
  all: () => [...registry.values()].map(x => x.meta),
  ids: () => [...registry.keys()],
  has: id => registry.has(id),
  /** 按支持的练习类型筛选 */
  supports: kind => [...registry.values()].filter(x => x.meta.supports?.includes(kind)).map(x => x.meta),
};

/**
 * 游戏基类：统一管理题目队列、作答判定、结算回调
 * 子类只需实现 renderRound() 与 finishGame()
 */
export class GameBase {
  /**
   * @param {object} ctx
   * @param {HTMLElement} ctx.root
   * @param {object[]} ctx.questions  已生成的题目（复用练习生成器）
   * @param {(result:object)=>void} ctx.onFinish
   */
  constructor({ root, questions = [], onFinish = () => {} }) {
    this.root = root;
    this.questions = questions;
    this.onFinish = onFinish;
    this.index = 0;
    this.results = [];
    this.destroyed = false;
    this._timers = [];
  }

  get total() { return this.questions.length; }
  get current() { return this.questions[this.index] || null; }

  /** 计时器统一登记，destroy 时清掉，避免切页后回调报错 */
  later(fn, ms) {
    const t = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this._timers.push(t);
    return t;
  }

  /** 判分（复用统一判分器） */
  judge(q, response) {
    return grade(q, response);
  }

  /** 记录一次作答 */
  record(q, correct, response) {
    this.results.push({
      questionId: q.id,
      refType: q.refType,
      refId: q.refId,
      unitId: q.unitId,
      level: q.level,
      skill: q.skill,
      type: q.type,
      prompt: q.prompt,
      expected: q.answer?.[0] || '',
      response: String(response ?? ''),
      correct,
      completion: correct ? 'independent' : 'failed',
      hintUsed: false,
      skipped: false,
      costMs: 0,
      answeredAt: new Date().toISOString(),
    });
    return correct;
  }

  /** 前进一题，返回是否还有题 */
  next() {
    this.index += 1;
    return this.index < this.questions.length;
  }

  finish(extra = {}) {
    const correct = this.results.filter(r => r.correct).length;
    this.onFinish({
      module: 'game',
      correct,
      total: this.results.length,
      records: this.results,
      ...extra,
    });
  }

  destroy() {
    this.destroyed = true;
    this._timers.forEach(clearTimeout);
    this._timers = [];
    if (this.root) this.root.innerHTML = '';
  }
}
